import { useEffect, useRef } from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import "video.js/dist/video-js.css";
import "./player.css";
import { updateLessonProgress } from "../../services/video-progress";

interface PlayerProps {
  src?: string;
  poster?: string;
  storageKey?: string;
  onProgress?: (percent: number) => void;
  lessonId?: number;
}

const VideoPlayer = ({ src, poster, storageKey, onProgress, lessonId }: PlayerProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<Player | null>(null);
  const maxWatchedTimeRef = useRef<number>(0);
  const lastSaveTsRef = useRef<number>(0);
  const lastServerSaveTsRef = useRef<number>(0);
  const serverInFlightRef = useRef<boolean>(false);
  const storageKeyRef = useRef<string | null>(null);
  const isSeekingRef = useRef<boolean>(false);
  const snappingRef = useRef<boolean>(false);
  const lastReportedProgressRef = useRef<number>(-1);
  // Delay persisting until we've had a chance to restore from saved position
  const resumeReadyRef = useRef<boolean>(false);

  // Install a VHS request hook ONCE to rewrite HLS requests from old domain to new domain
  // This ensures any URIs found inside master or media playlists that point to the old domain
  // are transparently fetched from the new domain instead.
  const installVhsRewriteHookOnce = () => {
    const vjsAny = videojs as any;
    // Guard against multiple installs
    if ((vjsAny as any).__vacVhsRewriteInstalled) return;
    try {
      const Vhs = vjsAny.Vhs || vjsAny.Hls; // older builds used Hls alias
      if (Vhs && Vhs.xhr) {
        const originalBeforeRequest = Vhs.xhr.beforeRequest;
        Vhs.xhr.beforeRequest = (options: any) => {
          try {
            const OLD_DOMAIN = "s3.ir-thr-at1.arvanstorage.ir/vac-video-storage";
            const NEW_DOMAIN = "vod.vac.academy";
            if (options) {
              if (typeof options.uri === "string" && options.uri.includes(OLD_DOMAIN)) {
                options.uri = options.uri.replace(OLD_DOMAIN, NEW_DOMAIN);
              } else if (typeof options.url === "string" && options.url.includes(OLD_DOMAIN)) {
                options.url = options.url.replace(OLD_DOMAIN, NEW_DOMAIN);
              }
            }
          } catch (_) {
            // noop
          }
          return originalBeforeRequest ? originalBeforeRequest(options) : options;
        };
        (vjsAny as any).__vacVhsRewriteInstalled = true;
      }
    } catch (_) {
      // If anything fails here, don't block playback
    }
  };

  // Function to save progress to localStorage
  const saveProgressToLocalStorage = (currentTime: number) => {
    if (!storageKeyRef.current) return;

    const lastVideoViewSec = Math.floor(currentTime);
    const data = {
      maxVideoViewSec: Math.floor(maxWatchedTimeRef.current),
      lastVideoViewSec,
    };
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(data));
    } catch (error) {
      console.error("Failed to save progress:", error);
    }
  };

  // Function to save progress to server (throttled)
  const saveProgressToServer = async (currentTime: number, opts?: { force?: boolean }) => {
    try {
      if (!lessonId) return; // Need lesson id to report progress
      const now = Date.now();
      const force = !!opts?.force;

      const lastVideoViewSec = Math.floor(currentTime);
      if (lastVideoViewSec < 0) return;

      // Prevent duplicate sends at the throttle boundary and during inflight
      if (serverInFlightRef.current) return;
      if (!force && now - lastServerSaveTsRef.current < 5000) return;

      // Mark inflight and reserve timestamp immediately to avoid race
      serverInFlightRef.current = true;
      if (!force) {
        lastServerSaveTsRef.current = now;
      }

      await updateLessonProgress(lessonId, lastVideoViewSec);
      // On success, update last save time
      lastServerSaveTsRef.current = Date.now();
    } catch (err) {
      // Silently ignore network/auth errors to avoid interrupting playback
    } finally {
      serverInFlightRef.current = false;
    }
  };



  useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current && videoRef.current) {
  // Ensure VHS hook is in place before any network requests
  installVhsRewriteHookOnce();
  const videoElement = document.createElement("video-js");
  videoElement.classList.add("vjs-big-play-centered", "vjs-compact");
      videoElement.style.width = "100%";
      videoElement.style.height = "100%";
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, {
        controls: true,
        responsive: false,
        fluid: false,
        fill: true,
        autoplay: true, // Autoplay on page load
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        html5: {
          vhs: {
            overrideNative: true,
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false,
        },
      }) as Player);

  // Build storage key for persistence (prefer explicit storageKey prop, fallback to lessonId)
  const computeStorageKey = (lessonId?: number, explicit?: string) => explicit ?? (lessonId ? `lesson_${lessonId}` : null);
  storageKeyRef.current = computeStorageKey(lessonId, storageKey);

      // Set source if provided
      if (src) {
        player.src({
          src: src,
          type: "application/x-mpegURL",
        });
      }

      // Set poster if provided
      if (poster) {
        player.poster(poster);
      }

      // Helpers for live/VOD seeking boundaries
  const LIVE_EDGE_SAFETY = 2; // seconds from live edge
  const FORWARD_LEEWAY = 1; // user can jump up to 1s ahead of max watched
      const getSeekableWindow = () => {
        try {
          const tr = player.seekable();
          if (tr && tr.length > 0) {
            return { start: tr.start(0) || 0, end: tr.end(0) || 0 };
          }
        } catch (_) {}
        return { start: 0, end: player.duration() || 0 };
      };

      const isLiveContent = () => {
        const d = player.duration();
        const isInf = typeof d === 'number' ? !isFinite(d) : true;
        return isInf || (player as any).liveTracker?.isLive();
      };

      const getLiveCap = () => {
        const { end } = getSeekableWindow();
        return isLiveContent() ? Math.max(0, end - LIVE_EDGE_SAFETY) : Number.POSITIVE_INFINITY;
      };

      // Hard cap at what is truly allowed (no leeway)
      const getAllowedMaxTime = () => {
        const liveCap = getLiveCap();
        return Math.min(maxWatchedTimeRef.current, liveCap);
      };

      // Softer cap that permits a small forward leeway
      const getAllowedForwardCap = () => {
        const liveCap = getLiveCap();
        return Math.min(maxWatchedTimeRef.current + FORWARD_LEEWAY, liveCap);
      };

      // Track maximum watched time and clamp forward jumps on each timeupdate
      player.on("timeupdate", () => {
        const currentTime = player.currentTime() || 0;

        // First, if actually playing forward (not seeking), move the ceiling up
        if (!isSeekingRef.current && !player.paused()) {
          if (currentTime > maxWatchedTimeRef.current) {
            maxWatchedTimeRef.current = currentTime;
          }
        }

        const allowedForward = getAllowedForwardCap();

        // Clamp if jumped too far ahead of the soft cap
        if (currentTime > allowedForward + 0.05 && !snappingRef.current) {
          snappingRef.current = true;
          player.currentTime(allowedForward);
          // slight defer to release snap flag
          setTimeout(() => { snappingRef.current = false; }, 0);
          return;
        }

        // Throttled persistence to localStorage (only after resume attempt)
        const now = Date.now();
        if (resumeReadyRef.current && storageKeyRef.current && now - lastSaveTsRef.current > 1000) {
          saveProgressToLocalStorage(currentTime);
          lastSaveTsRef.current = now;
        }

        // Throttled persistence to server (only after resume attempt)
        if (resumeReadyRef.current) {
          void saveProgressToServer(currentTime);
        }

        // Emit progress percent (based on max watched vs VOD duration or seekable span)
        try {
          if (typeof onProgress === 'function') {
            const d = player.duration();
            const live = !(typeof d === 'number' && isFinite(d)) || (player as any).liveTracker?.isLive();
            let denom = 0;
            if (live) {
              const { start, end } = getSeekableWindow();
              denom = Math.max(0, end - start);
            } else {
              denom = typeof d === 'number' && isFinite(d) ? d : 0;
            }
            if (denom > 0) {
              const watched = Math.min(maxWatchedTimeRef.current, denom);
              const percent = Math.max(0, Math.min(100, (watched / denom) * 100));
              if (Math.abs(percent - lastReportedProgressRef.current) >= 0.5) {
                lastReportedProgressRef.current = percent;
                onProgress(percent);
              }
            }
          }
        } catch (_) {}
      });

      // Prevent seeking forward beyond watched time (including live edge cap)
      player.on("seeking", () => {
        isSeekingRef.current = true;
        const currentTime = player.currentTime() || 0;
        const allowedForward = getAllowedForwardCap();
        if (currentTime > allowedForward) {
          snappingRef.current = true;
          player.currentTime(allowedForward);
          setTimeout(() => { snappingRef.current = false; }, 0);
        }
      });

      // Additional protection: after seek completes
      player.on("seeked", () => {
        const currentTime = player.currentTime() || 0;
        const allowedForward = getAllowedForwardCap();
        if (currentTime > allowedForward) {
          snappingRef.current = true;
          player.currentTime(allowedForward);
          setTimeout(() => { snappingRef.current = false; }, 0);
        }
        isSeekingRef.current = false;
      });

      // Override progress bar click behavior
      player.ready(() => {
        const progressControl = (player as any).controlBar.progressControl;
        if (progressControl) {
          const seekBar = progressControl.seekBar;
          if (seekBar && seekBar.el_) {
            const seekBarEl = seekBar.el_;
            
            // Create visual indicator for max watched position
            const maxWatchedIndicator = document.createElement('div');
            maxWatchedIndicator.className = 'vjs-max-watched-indicator';
            maxWatchedIndicator.style.cssText = `
              position: absolute;
              top: 0;
              bottom: 0;
              width: 2px;
              background-color: rgba(255, 255, 255, 0.5);
              pointer-events: none;
              z-index: 2;
            `;
            seekBarEl.appendChild(maxWatchedIndicator);

            // Update indicator position
            const updateMaxWatchedIndicator = () => {
              const d = player.duration();
              const live = !(typeof d === 'number' && isFinite(d)) || (player as any).liveTracker?.isLive();
              if (live) {
                const { start, end } = getSeekableWindow();
                const span = Math.max(end - start, 0.001);
                const clamped = Math.min(Math.max(maxWatchedTimeRef.current, start), end);
                const pct = ((clamped - start) / span) * 100;
                maxWatchedIndicator.style.left = `${Math.min(Math.max(pct, 0), 100)}%`;
              } else {
                const duration = player.duration() || 0;
                if (duration > 0) {
                  const percentage = (maxWatchedTimeRef.current / duration) * 100;
                  maxWatchedIndicator.style.left = `${Math.min(Math.max(percentage, 0), 100)}%`;
                }
              }
            };

            player.on('timeupdate', updateMaxWatchedIndicator);
            player.on('durationchange', updateMaxWatchedIndicator);

            // Intercept mousedown on the progress bar
            const computeTargetFromX = (clientX: number) => {
              const rect = seekBarEl.getBoundingClientRect();
              const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
              if (isLiveContent()) {
                const { start, end } = getSeekableWindow();
                return start + ratio * (end - start);
              }
              const duration = player.duration() || 0;
              return ratio * duration;
            };

            const handleMouseDown = (e: MouseEvent) => {
              const targetTime = computeTargetFromX(e.clientX);
              const allowedMax = getAllowedForwardCap();
              if (targetTime > allowedMax + 0.1) {
                e.stopImmediatePropagation();
                e.preventDefault();
                player.currentTime(allowedMax);
                return false;
              }
            };

            const handleTouchStart = (e: TouchEvent) => {
              const touch = e.touches[0];
              const targetTime = computeTargetFromX(touch.clientX);
              const allowedMax = getAllowedForwardCap();
              if (targetTime > allowedMax + 0.1) {
                e.stopImmediatePropagation();
                e.preventDefault();
                player.currentTime(allowedMax);
                return false;
              }
            };

            seekBarEl.addEventListener('pointerdown', (e: PointerEvent) => {
              const targetTime = computeTargetFromX(e.clientX);
              const allowedMax = getAllowedMaxTime();
              if (targetTime > allowedMax + 0.1) {
                e.stopImmediatePropagation();
                e.preventDefault();
                player.currentTime(allowedMax);
                return false;
              }
            }, true);

            seekBarEl.addEventListener('mousedown', handleMouseDown, true);
            seekBarEl.addEventListener('touchstart', handleTouchStart, true);
            
            // Also prevent click events
            seekBarEl.addEventListener('click', (e: MouseEvent) => {
              const targetTime = computeTargetFromX(e.clientX);
              const allowedMax = getAllowedForwardCap();
              if (targetTime > allowedMax + 0.1) {
                e.stopImmediatePropagation();
                e.preventDefault();
                player.currentTime(allowedMax);
                return false;
              }
            }, true);
          }
        }
      });

      // Block forward-seek via keyboard shortcuts (ArrowRight, digits, End, L)
      player.ready(() => {
  const el = ((player as any).el && (player as any).el()) as HTMLElement | undefined;
        if (!el) return;

  const tolerance = 0.1; // seconds
        const stepSec = 5; // default arrow right seek step in video.js

        const keydownHandler = (e: KeyboardEvent) => {
          const { start, end } = getSeekableWindow();
          const isLive = isLiveContent();
          const duration = isLive ? end - start : (player.duration() || 0);
          const current = player.currentTime() || 0;
          let target: number | null = null;

          switch (e.key) {
            case 'ArrowRight':
              target = current + stepSec;
              break;
            case 'End':
              target = isLive ? end : duration;
              break;
            case 'l':
            case 'L':
              // Some UIs use L to seek forward 10s
              target = current + 10;
              break;
            default: {
              // Number keys 0-9 jump to percentage in some players
              if (e.key >= '0' && e.key <= '9' && duration > 0) {
                const digit = parseInt(e.key, 10);
                target = isLive ? start + (digit / 10) * (end - start) : (digit / 10) * duration;
              }
              break;
            }
          }

          const allowedMax = getAllowedForwardCap();
          if (target !== null && target > allowedMax + tolerance) {
            e.preventDefault();
            // Use stopImmediatePropagation to beat other listeners
            if (typeof (e as any).stopImmediatePropagation === 'function') {
              (e as any).stopImmediatePropagation();
            } else {
              e.stopPropagation();
            }
            // Snap back if something advanced the time already
            player.currentTime(allowedMax);
          }
        };

        // Capture phase intercept to pre-empt internal handlers
        el.addEventListener('keydown', keydownHandler, true);
        player.on('dispose', () => {
          el.removeEventListener('keydown', keydownHandler, true);
        });
      });

      // Restore last watched time on metadata load (after durations known)
      player.on("loadedmetadata", () => {
        try {
          if (!storageKeyRef.current) {
            // No storage key, allow saving immediately
            resumeReadyRef.current = true;
            return;
          }
          
          const raw = localStorage.getItem(storageKeyRef.current);
          if (!raw) {
            // No saved data, allow saving immediately
            resumeReadyRef.current = true;
            return;
          }
          
          const parsed = JSON.parse(raw) as { maxVideoViewSec?: number; lastVideoViewSec?: number } | null;
          const savedMax = typeof parsed?.maxVideoViewSec === 'number' ? parsed!.maxVideoViewSec! : 0;
          const savedLast = typeof parsed?.lastVideoViewSec === 'number' ? parsed!.lastVideoViewSec! : 0;
          
          if (savedMax > 0) {
            // Initialize max watched from saved
            maxWatchedTimeRef.current = savedMax;
          }
          
          // Start from lastVideoViewSec only
          if (savedLast > 0) {
            const startPosition = savedLast;
            // For live, clamp to live cap
            const { end, start } = ((): { start: number; end: number } => {
              try {
                const tr = player.seekable();
                if (tr && tr.length > 0) return { start: tr.start(0) || 0, end: tr.end(0) || 0 };
              } catch (_) {}
              return { start: 0, end: player.duration() || 0 };
            })();
            const d = player.duration();
            const live = !(typeof d === 'number' && isFinite(d)) || (player as any).liveTracker?.isLive();
            const liveCap = live ? Math.max(0, end - 2) : Number.POSITIVE_INFINITY;
            const target = live ? Math.min(Math.max(startPosition, start), liveCap) : startPosition;
            
            // Seek to saved position
            player.currentTime(target);
            
            // Add a small delay before allowing saves
            // This prevents the initial timeupdate at 0 from overwriting our saved position
            setTimeout(() => {
              resumeReadyRef.current = true;
              // Do not auto-play; wait for user interaction
            }, 100);
          } else {
            // No saved position, start from beginning and allow saving immediately
            resumeReadyRef.current = true;
          }

          // Emit initial progress now that duration is known
          if (typeof onProgress === 'function') {
            const d = player.duration();
            const live = !(typeof d === 'number' && isFinite(d)) || (player as any).liveTracker?.isLive();
            let denom = 0;
            if (live) {
              const { start, end } = getSeekableWindow();
              denom = Math.max(0, end - start);
            } else {
              denom = typeof d === 'number' && isFinite(d) ? d : 0;
            }
            if (denom > 0) {
              const watched = Math.min(maxWatchedTimeRef.current, denom);
              const percent = Math.max(0, Math.min(100, (watched / denom) * 100));
              lastReportedProgressRef.current = percent;
              onProgress(percent);
            }
          }
        } catch (_) {
          // On error, allow saving
          resumeReadyRef.current = true;
        }
      });

      // Add custom quality selector
      player.ready(() => {
        const qualityLevels = (player as any).qualityLevels();
        
        // Create quality menu button
        const MenuButton = videojs.getComponent("MenuButton");
        const MenuItem = videojs.getComponent("MenuItem");
        const Button = videojs.getComponent("Button");

        // Create custom seek back button
        class SeekBackButton extends Button {
          constructor(player: any, options: any) {
            super(player, options);
            (this as any).controlText("Seek back 10 seconds");
          }

          buildCSSClass() {
            return `vjs-seek-back-button vjs-control vjs-button ${super.buildCSSClass()}`;
          }

          handleClick() {
            const currentTime = this.player().currentTime() || 0;
            const seekTime = Math.max(0, currentTime - 10);
            this.player().currentTime(seekTime);
          }

          createEl() {
            const el = super.createEl("button", {
              className: "vjs-seek-back-button vjs-control vjs-button",
            });
            
            // Add icon (using SVG or text)
            el.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="vjs-icon-placeholder" style="width: 100%; height: 100%;">
                <path d="M11.99 5V1l-5 5 5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6h-2c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                <text x="12" y="16" text-anchor="middle" font-size="8" font-weight="bold" fill="currentColor">10</text>
              </svg>
            `;
            
            return el;
          }
        }

        // Register the seek back button component
        videojs.registerComponent("SeekBackButton", SeekBackButton);

        class QualityMenuItem extends MenuItem {
          item: any;
          qualityLevels: any;
          label: string;

          constructor(player: any, options: any) {
            super(player, options);
            this.item = options.item;
            this.qualityLevels = options.qualityLevels;
            this.label = options.label;
          }

          handleClick() {
            const qualityLevels = this.qualityLevels;
            
            if (this.item === "auto") {
              // Enable auto quality
              for (let i = 0; i < qualityLevels.length; i++) {
                qualityLevels[i].enabled = true;
              }
            } else {
              // Disable all, then enable selected quality
              for (let i = 0; i < qualityLevels.length; i++) {
                qualityLevels[i].enabled = false;
              }
              this.item.enabled = true;
            }
          }

          update() {
            const qualityLevels = this.qualityLevels;
            let selected = false;

            if (this.item === "auto") {
              // Check if auto (multiple levels enabled)
              let enabledCount = 0;
              for (let i = 0; i < qualityLevels.length; i++) {
                if (qualityLevels[i].enabled) {
                  enabledCount++;
                }
              }
              selected = enabledCount > 1 || enabledCount === qualityLevels.length;
            } else {
              selected = this.item.enabled;
            }

            (this as any).selected(selected);
          }
        }

        class QualityMenuButton extends MenuButton {
          constructor(player: any, options: any) {
            super(player, options);
            (this as any).controlText("Quality");
            
            const qualityLevels = (player as any).qualityLevels();
            
            // Update button when quality changes
            qualityLevels.on("change", () => {
              this.updateLabel();
              (this as any).update();
            });
          }

          // Create element and inject an inline SVG "SD" badge so the button always shows a logo
          createEl() {
            const el = super.createEl();
            try {
              // Avoid duplicating icon on re-renders
              if (!el.querySelector('.vjs-quality-icon')) {
                const iconWrap = document.createElement('span');
                iconWrap.className = 'vjs-quality-icon';
                iconWrap.setAttribute('aria-hidden', 'true');
                iconWrap.style.display = 'inline-flex';
                iconWrap.style.alignItems = 'center';
                iconWrap.style.justifyContent = 'center';
                iconWrap.style.width = '22px';
                iconWrap.style.height = '18px';
                iconWrap.style.marginTop = '6px';
                // iconWrap.style.marginRight = '4px';
                iconWrap.style.flex = '0 0 auto';
                iconWrap.innerHTML = `
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 20" width="22" height="18" aria-hidden="true">
                    <rect x="0.75" y="0.75" width="30.5" height="18.5" rx="4" fill="none" stroke="currentColor" stroke-width="1.5"/>
                    <text x="16" y="13" text-anchor="middle" font-size="11" font-weight="700" fill="currentColor" font-family="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif">SD</text>
                  </svg>
                `;
                // Make the SD badge act like the button itself
                iconWrap.style.cursor = 'pointer';
                iconWrap.addEventListener('click', (ev) => {
                  try {
                    ev.preventDefault();
                    ev.stopPropagation();
                    // Delegate to the MenuButton click handler to toggle the menu
                    (this as any).handleClick(ev as any);
                  } catch (_) {}
                });
                // Insert at the start so text/label follows
                el.insertBefore(iconWrap, el.firstChild);
              }
            } catch (_) {}
            return el;
          }

          updateLabel() {
            const qualityLevels = (this.player() as any).qualityLevels();
            let currentQuality = "Auto";
            
            // Count enabled quality levels
            let enabledCount = 0;
            let lastEnabledQuality: any = null;
            
            for (let i = 0; i < qualityLevels.length; i++) {
              if (qualityLevels[i].enabled) {
                enabledCount++;
                lastEnabledQuality = qualityLevels[i];
              }
            }
            
            // If only one quality is enabled, show that quality
            // Otherwise show "Auto"
            if (enabledCount === 1 && lastEnabledQuality) {
              currentQuality = lastEnabledQuality.height + "p";
            }
            
            // Update button text
            const menuButtonTextEl = (this as any).el().querySelector('.vjs-menu-button-text');
            if (menuButtonTextEl) {
              menuButtonTextEl.textContent = currentQuality;
            } else {
              // Create text element if it doesn't exist
              const textEl = document.createElement('span');
              textEl.className = 'vjs-menu-button-text';
              textEl.textContent = currentQuality;
              (this as any).el().appendChild(textEl);
            }
          }

          buildCSSClass() {
            return `vjs-quality-button ${super.buildCSSClass()}`;
          }

          createItems() {
            const qualityLevels = (this.player() as any).qualityLevels();
            const items: any[] = [];

            // Add Auto option
            items.push(
              new QualityMenuItem(this.player(), {
                label: "Auto",
                item: "auto",
                qualityLevels: qualityLevels,
                selectable: true,
              })
            );

            // Add quality options
            for (let i = 0; i < qualityLevels.length; i++) {
              const quality = qualityLevels[i];
              items.push(
                new QualityMenuItem(this.player(), {
                  label: quality.height + "p",
                  item: quality,
                  qualityLevels: qualityLevels,
                  selectable: true,
                })
              );
            }

            return items;
          }
        }

        // Register components
        videojs.registerComponent("QualityMenuItem", QualityMenuItem);
        videojs.registerComponent("QualityMenuButton", QualityMenuButton);

        // Add seek back button to control bar
        const addSeekBackButton = () => {
          const controlBar = (player as any).controlBar;
          const playToggle = controlBar.getChild("playToggle");
          
          if (playToggle && !controlBar.getChild("SeekBackButton")) {
            const seekBackButton = controlBar.addChild(
              "SeekBackButton",
              {},
              controlBar.children().indexOf(playToggle) + 1
            );
            seekBackButton.addClass("vjs-seek-back-button");
          }
        };

        // Add quality button to control bar after a short delay to ensure qualityLevels are loaded
        const addQualityButton = () => {
          if (qualityLevels && qualityLevels.length > 0) {
            const controlBar = (player as any).controlBar;
            const fullscreenToggle = controlBar.getChild("fullscreenToggle");
            
            if (fullscreenToggle) {
              const qualityButton = controlBar.addChild(
                "QualityMenuButton",
                {},
                controlBar.children().indexOf(fullscreenToggle)
              );
              qualityButton.addClass("vjs-quality-button");
              // Initialize the label
              setTimeout(() => {
                (qualityButton as any).updateLabel();
              }, 100);
            }
          }
        };

        // Add seek back button immediately
        addSeekBackButton();

        // Wait for quality levels to be available
        qualityLevels.on("addqualitylevel", () => {
          if (qualityLevels.length > 0 && !(player as any).controlBar.getChild("QualityMenuButton")) {
            addQualityButton();
          }
        });

        // Try to add immediately if levels are already available
        if (qualityLevels.length > 0) {
          addQualityButton();
        }
      });

      // Error handling
      player.on("error", () => {
        const error = player.error();
        console.error("Video.js error:", error);
      });

      // Handle video completion
      player.on("ended", () => {
        const currentTime = maxWatchedTimeRef.current;
        saveProgressToLocalStorage(currentTime);
        // Force a final server sync on end
        void saveProgressToServer(currentTime, { force: true });
      });



      // Log quality levels when they're available
      player.on("loadedmetadata", () => {
        const qualityLevels = (player as any).qualityLevels();
        if (qualityLevels && qualityLevels.length > 0) {
          console.log("Available quality levels:", qualityLevels.length);
          for (let i = 0; i < qualityLevels.length; i++) {
            console.log(`Quality ${i}:`, qualityLevels[i].height + "p");
          }
        }
      });
    }
  }, []);

  // Update source when it changes
  useEffect(() => {
    const player = playerRef.current;
    if (player && src) {
      // Save progress before changing lesson (if there's meaningful progress)
      const currentTime = player.currentTime() || 0;
      if (currentTime > 0 && maxWatchedTimeRef.current > 0) {
        saveProgressToLocalStorage(currentTime);
      }

      // Pause the player before changing source
      player.pause();

      // IMPORTANT: Block saving immediately to prevent 0 from being saved
      resumeReadyRef.current = false;

      // Reset all lesson-specific states
      maxWatchedTimeRef.current = 0;
      lastSaveTsRef.current = 0;
      isSeekingRef.current = false;
      snappingRef.current = false;
      lastReportedProgressRef.current = -1;

      // Load persisted max watched time for new lesson
      const key = storageKey ?? (lessonId ? `lesson_${lessonId}` : null);
      storageKeyRef.current = key;

      if (key) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw) as { maxVideoViewSec?: number; lastVideoViewSec?: number } | null;
            const savedMax = typeof parsed?.maxVideoViewSec === 'number' ? parsed!.maxVideoViewSec! : 0;
            if (savedMax > 0) maxWatchedTimeRef.current = savedMax;
          }
        } catch (_) {}
      }

      // Set new source (do NOT reset currentTime to 0 yet)
      player.src({
        src: src,
        type: "application/x-mpegURL",
      });

      // Load the new source
      player.load();
    }
  }, [src, storageKey, lessonId]);

  // Update poster when it changes
  useEffect(() => {
    const player = playerRef.current;
    if (player && poster) {
      player.poster(poster);
    }
  }, [poster]);

  // Dispose the Video.js player when the component unmounts
  useEffect(() => {
    const player = playerRef.current;

    // Handle browser/tab close - save progress
    const handleBeforeUnload = () => {
      const currentTime = player?.currentTime() || 0;
      saveProgressToLocalStorage(currentTime);
      void saveProgressToServer(currentTime, { force: true });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      
      if (player && !player.isDisposed()) {
        // Save progress on component unmount
        const currentTime = player.currentTime() || 0;
        saveProgressToLocalStorage(currentTime);
        void saveProgressToServer(currentTime, { force: true });

        // Persist on dispose
        try {
          if (storageKeyRef.current) {
            const data = {
              maxVideoViewSec: Math.floor(maxWatchedTimeRef.current),
              lastVideoViewSec: Math.floor(currentTime),
            };
            localStorage.setItem(storageKeyRef.current, JSON.stringify(data));
          }
        } catch (_) {}
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [lessonId]);

  return (
    <div className="w-full h-full bg-[#242424] rounded-4xl overflow-hidden p-1 md:p-2" id="video-section">
      <div data-vjs-player className="w-full h-full">
        <div ref={videoRef} className="w-full h-full" />
      </div>
    </div>
  );
};

export default VideoPlayer;
